import React, { useEffect, useState } from 'react';

import { FaTrash } from 'react-icons/fa';

import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { alpha, styled } from '@mui/material/styles';
import { TreeItem, TreeItemProps, treeItemClasses } from '@mui/x-tree-view/TreeItem';
import { Box, Typography } from '@mui/material';
import FolderRounded from '@mui/icons-material/FolderRounded';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { v4 as uuidv4 } from 'uuid';
import { InferenceEngineFileTypes } from '../../enums';
import { Package_Definition_File_Name } from '../../constants';

type StyledTreeItemProps = Omit<TreeItemProps, 'label'> & {
  labelIcon: React.ElementType;
  labelText: string;
  deleteIcon?: React.ElementType;
};

const StyledTreeItemRoot = styled(TreeItem)(({ theme }) => ({
  color: theme.palette.mode === 'light' ? theme.palette.grey[800] : theme.palette.grey[400],
  position: 'relative',
  [`& .${treeItemClasses.content}`]: {
    flexDirection: 'row-reverse',
    borderRadius: theme.spacing(0.7),
    marginBottom: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(0.5),
    paddingRight: theme.spacing(1),
    fontWeight: 500,
    [`& .${treeItemClasses.label}`]: {
      fontWeight: 'inherit',
    },
    [`& .${treeItemClasses.iconContainer}`]: {
      marginRight: theme.spacing(2),
    },
    [`&.Mui-expanded `]: {
      '&:not(.Mui-focused, .Mui-selected, .Mui-selected.Mui-focused) .labelIcon': {
        color: theme.palette.mode === 'light' ? theme.palette.primary.main : theme.palette.secondary.dark,
      },
      '&::before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        left: '16px',
        top: '44px',
        height: 'calc(100% - 48px)',
        width: '1.5px',
        backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
      },
    },
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      color: theme.palette.mode === 'light' ? theme.palette.primary.main : 'white',
    },
    [`&.Mui-focused, &.Mui-selected, &.Mui-selected.Mui-focused`]: {
      backgroundColor: theme.palette.mode === 'light' ? theme.palette.primary.main : theme.palette.secondary.dark,
      color: 'white',
    },
  },
  [`& .${treeItemClasses.groupTransition}`]: {
    marginLeft: theme.spacing(3.5),
    [`& .${treeItemClasses.content}`]: {
      fontWeight: 500,
    },
  },
})) as unknown as typeof TreeItem;

const StyledTreeItemLabel = styled(Typography)({
  color: 'inherit',
  fontFamily: 'General Sans',
  fontWeight: 'inherit',
  flexGrow: 1,
}) as unknown as typeof Typography;

const StyledTreeItem = React.forwardRef(function StyledTreeItem(props: StyledTreeItemProps, ref: React.Ref<HTMLLIElement>) {
  const { labelIcon: LabelIcon, labelText, deleteIcon: DeleteIcon, ...other } = props;

  return (
    <StyledTreeItemRoot
      label={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box component={LabelIcon} className="labelIcon" color="inherit" sx={{ mr: 1, fontSize: '1.2rem' }} />
          <StyledTreeItemLabel variant="body2">{labelText}</StyledTreeItemLabel>
        </Box>
      }
      {...other}
      ref={ref}
    />
  );
});

interface Iprops {}

const EngineFileList = ({ files, onTreeItemLoad, onLastItemUpdate }) => {
  const [fileList, setFileList] = useState([]);
  const [items, setItems] = useState([]);
  const [lastSelectedItem, setLastSelectedItem] = React.useState<string | null>(null);
  const [expandedList, setExpandedList] = useState(['1', '2']);

  const handleItemSelectionToggle = (event: React.SyntheticEvent, itemId: string, isSelected: boolean) => {
    if (isSelected) {
      setLastSelectedItem(itemId);
      onLastItemUpdate(itemId);
    }
  };

  const getFileList = (type) => {
    return files
      .filter((file) => file.fileType === type)
      .map((f) => {
        return {
          labelText: f.fileName,
          itemId: uuidv4(),
          labelIcon: InsertDriveFileIcon,
          type: type,
          fileName: f.fileName,
        };
      });
  };
  const packageDefinitionFile = {
    labelText: 'Package Definition File',
    itemId: '3',
    labelIcon: InsertDriveFileIcon,
    type: InferenceEngineFileTypes.PackageDefinitionFile,
    fileName: Package_Definition_File_Name,
  };
  const platFormFile = {
    labelText: 'Platform.py',
    itemId: '4',
    labelIcon: InsertDriveFileIcon,
    type: InferenceEngineFileTypes.PlatformFile,
    fileName: 'Platform.py',
  };
  const generateTreeListData = (files) => {
    let sourceFolder = {
      labelText: 'Source Files',
      itemId: '1',
      labelIcon: FolderRounded,
      type: 'Folder',
      children: [platFormFile],
    };
    let modelFolder = {
      labelText: 'Model Files',
      itemId: '2',
      labelIcon: FolderRounded,
      type: 'Folder',
      children: [],
    };
    if (files && files.length > 0) {
      const sourceFileList = getFileList(InferenceEngineFileTypes.SourceFile);
      const newSourceFileList = sourceFileList.concat(sourceFolder.children);
      sourceFolder.children = newSourceFileList;
      const modelFileList = getFileList(InferenceEngineFileTypes.ModelFiles);
      modelFolder.children = modelFileList;
    }
    let list = [sourceFolder, modelFolder, packageDefinitionFile];
    setItems(list);
  };

  const onTreeItemClick = (e, item) => {
    if (item && item.type !== 'Folder') {
      // console.log('onTreeItemClick', { e, item });
      onTreeItemLoad(item);
    }
  };

  useEffect(() => {
    // console.log('files', { files, items, lastSelectedItem });
    if (files && files.length !== fileList.length) {
      setFileList(files);
      generateTreeListData(files);
    }
    if (!lastSelectedItem && files) onTreeItemLoad(packageDefinitionFile);
  }, [files]);

  return (
    <>
      <div className="tree-list-container">
        <SimpleTreeView
          aria-label="gmail"
          defaultExpandedItems={['3']}
          defaultSelectedItems="3"
          sx={{ height: 'fit-content', flexGrow: 1, overflowY: 'auto' }}
          onItemSelectionToggle={handleItemSelectionToggle}
          expandedItems={expandedList}
        >
          {items.map((item) => {
            return (
              <StyledTreeItem
                itemId={item.itemId}
                labelText={item.labelText}
                labelIcon={item.labelIcon}
                onClick={(e) => onTreeItemClick(e, item)}
                key={item.itemId}
              >
                {item.type === 'Folder' &&
                  item.children.map((file) => {
                    return (
                      <StyledTreeItem
                        // sx={{ cursor: file.type === InferenceEngineFileTypes.ModelFiles ? 'not-allowed' : 'pointer' }}
                        // disabled={file.type === InferenceEngineFileTypes.ModelFiles}
                        key={file.itemId}
                        itemId={file.itemId}
                        labelText={file.labelText}
                        labelIcon={file.labelIcon}
                        onClick={(e) => onTreeItemClick(e, file)}
                      />
                    );
                  })}
              </StyledTreeItem>
            );
          })}
        </SimpleTreeView>
      </div>
    </>
  );
};

export default EngineFileList;
